import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { post } from './client';
import type {
  ConsultDoctorOption,
  ConsultUrgency,
  EvidenceModule,
  ExerciseRxItem,
  HealthPlanDetail,
  HealthPlanListItem,
  MedicalReference,
  MedicationItem,
  MonitoringItem,
  PagedResult,
  PatientEvidenceFact,
  ReviewReminderItem,
} from './model';
import { myPatientKeys } from './myPatient';
import { patientKeys } from './patient';

// --- 参数类型 ---

export interface HealthPlanListParams {
  pageNo?: number;
  pageSize?: number;
  status?: string;
  keyword?: string;
}

export interface HealthPlanUpdateParams {
  id: string;
  diet: string[];
  // 每日推荐摄入目标(千卡/天),null 表示清除设定、跟随公式估算
  dietCalorieTarget: number | null;
  nutrition: string[];
  tcmRegulation: string[];
  exerciseRx: ExerciseRxItem[];
  monitoring: MonitoringItem[];
  reviewReminders: ReviewReminderItem[];
  endDate?: string;
  recommendations?: { productId: string; reason: string }[];
  evidenceSelections?: { module: EvidenceModule; atomId: string }[];
}

export interface HealthPlanMedicalReferenceGroup {
  module: EvidenceModule;
  references: MedicalReference[];
  attachedAtomIds: string[];
  canAttach: boolean;
  emptyReason?: string;
}

export interface HealthPlanMedicalReferenceBundle {
  knowledgeVersion: string;
  facts: PatientEvidenceFact[];
  groups: HealthPlanMedicalReferenceGroup[];
  disclaimer: string;
}

export interface HealthPlanClaimEditParams {
  planId: string;
  confirmAddPatient?: boolean;
}

export interface HealthPlanClaimEditResult {
  status: 'ready' | 'need_claim_patient';
  message?: string;
}

// --- AI 生成结果（预览用，前端应用到编辑表单后再走 update 落库） ---
export interface AiGenerateResult {
  diet: string[];
  dietCalorieTarget: number | null;
  nutrition: string[];
  tcmRegulation: string[];
  exerciseRx: ExerciseRxItem[];
  monitoring: MonitoringItem[];
  medication: MedicationItem[];
  reviewReminders: ReviewReminderItem[];
}

export type HealthIssueStatus = 'current' | 'pending_review' | 'resolved';
export interface HealthIssueEvidence {
  id: string;
  reportId: string;
  date: string | null;
  reportName: string;
  problem: string;
  role: string;
  status: HealthIssueStatus;
}

export interface HealthIssueItem {
  id: string;
  title: string;
  status: HealthIssueStatus;
  abnormalGrade: string;
  priorityLevel: string;
  adviceLevel: string;
  timeframe: string;
  checkItems: string[];
  advice: string;
  sourceDate: string | null;
  sourceName: string;
  evidence: HealthIssueEvidence[];
}

export interface HealthIssueLedger {
  summary: string;
  issues: HealthIssueItem[];
  currentCount: number;
  pendingCount: number;
  resolvedCount: number;
}

export type HealthIssueLedgerCacheStatus =
  | 'idle'
  | 'running'
  | 'success'
  | 'failed';
export interface HealthIssueLedgerState {
  status: HealthIssueLedgerCacheStatus;
  stale: boolean;
  ledger: HealthIssueLedger;
  generatedAt: string | null;
  sourceUpdatedAt: string | null;
  lastStartedAt: string | null;
  errorMessage: string | null;
}

export interface HealthPlanOriginalReportField {
  label: string;
  value: string;
}

export interface HealthPlanOriginalReportLab {
  name: string;
  result: string;
  unit: string;
  reference: string;
  flag: string;
  note: string;
}

export interface HealthPlanOriginalReport {
  id: string;
  sourceType: string;
  title: string;
  date: string | null;
  meta: HealthPlanOriginalReportField[];
  sections: HealthPlanOriginalReportField[];
  labItems: HealthPlanOriginalReportLab[];
}

// --- queryKeys ---

export const healthPlanKeys = {
  all: ['healthPlan'] as const,
  list: (p: HealthPlanListParams) =>
    [...healthPlanKeys.all, 'list', p] as const,
  detail: (id: string) => [...healthPlanKeys.all, 'detail', id] as const,
  issueLedgerAI: (id: string) =>
    [...healthPlanKeys.all, 'issueLedgerAI', id] as const,
  medicalReferences: (id: string) =>
    [...healthPlanKeys.all, 'medicalReferences', id] as const,
  byPatient: (patientId: string) =>
    [...healthPlanKeys.all, 'byPatient', patientId] as const,
  consultDoctors: () => [...healthPlanKeys.all, 'consultDoctors'] as const,
};

// 方案 update / publish / claim-edit / consult-transfer / delete 都可能影响患者列表的
// owner / status / in_consultation，需同时失效 healthPlan / patient / myPatient 三处。
function invalidateHealthPlanRelated(qc: QueryClient) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: healthPlanKeys.all }),
    qc.invalidateQueries({ queryKey: patientKeys.all }),
    qc.invalidateQueries({ queryKey: myPatientKeys.all }),
  ]);
}

// --- 请求函数 ---

export const listHealthPlans = (p: HealthPlanListParams) =>
  post<PagedResult<HealthPlanListItem>>('/health-plan/list', p);

export const getHealthPlanDetail = (id: string) =>
  post<HealthPlanDetail>('/health-plan/detail', { id });

export const getHealthPlanMedicalReferences = (planId: string) =>
  post<HealthPlanMedicalReferenceBundle>('/health-plan/medical-references', {
    planId,
  });

export const updateHealthPlan = (data: HealthPlanUpdateParams) =>
  post<void>('/health-plan/update', data);

export const listHealthPlansByPatient = (patientId: string) =>
  post<HealthPlanListItem[]>('/health-plan/by-patient', { patientId });

export const aiGenerateHealthPlan = (id: string) =>
  post<AiGenerateResult>('/health-plan/ai-generate', { id });

export const aiGenerateHealthPlanReviewReminders = (id: string) =>
  post<ReviewReminderItem[]>('/health-plan/review-reminders/ai-generate', {
    id,
  });

export const getHealthPlanIssueLedgerAI = (planId: string) =>
  post<HealthIssueLedgerState>('/health-plan/issue-ledger-ai', { planId });

export const refreshHealthPlanIssueLedger = (planId: string) =>
  post<HealthIssueLedgerState>('/health-plan/issue-ledger-refresh', { planId });

export const getHealthPlanOriginalReport = (data: {
  planId: string;
  reportId: string;
}) => post<HealthPlanOriginalReport>('/health-plan/original-report', data);

export const publishHealthPlan = (data: { planId: string }) =>
  post<void>('/health-plan/publish', data);

// --- 会诊工作流 ---

export const listConsultDoctors = () =>
  post<ConsultDoctorOption[]>('/health-plan/consult-doctors');

export const claimEditHealthPlan = (
  params: string | HealthPlanClaimEditParams,
) => {
  const data = typeof params === 'string' ? { planId: params } : params;
  return post<HealthPlanClaimEditResult>('/health-plan/claim-edit', data);
};

export interface ConsultTransferParams {
  planId: string;
  consultDoctorId: string;
  urgency: ConsultUrgency;
  reason: string;
}

export const consultTransferHealthPlan = (data: ConsultTransferParams) =>
  post<void>('/health-plan/consult-transfer', data);

// --- Query hooks ---

export const useHealthPlanListQuery = (p: HealthPlanListParams) =>
  useQuery({
    queryKey: healthPlanKeys.list(p),
    queryFn: () => listHealthPlans(p),
  });

export const useHealthPlanDetailQuery = (id: string) =>
  useQuery({
    queryKey: healthPlanKeys.detail(id),
    queryFn: () => getHealthPlanDetail(id),
    enabled: !!id,
  });

export const useHealthPlanMedicalReferencesQuery = (id: string) =>
  useQuery({
    queryKey: healthPlanKeys.medicalReferences(id),
    queryFn: () => getHealthPlanMedicalReferences(id),
    enabled: !!id,
  });

export const useHealthPlanIssueLedgerAIQuery = (id: string) =>
  useQuery({
    queryKey: healthPlanKeys.issueLedgerAI(id),
    queryFn: () => getHealthPlanIssueLedgerAI(id),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data?.status === 'running' ? 3000 : 30000,
    staleTime: 30 * 1000,
  });

export const useHealthPlanByPatientQuery = (patientId: string) =>
  useQuery({
    queryKey: healthPlanKeys.byPatient(patientId),
    queryFn: () => listHealthPlansByPatient(patientId),
    enabled: !!patientId,
  });

export const useConsultDoctorsQuery = (enabled = true) =>
  useQuery({
    queryKey: healthPlanKeys.consultDoctors(),
    queryFn: listConsultDoctors,
    enabled,
  });

// --- Mutation hooks ---

export const useHealthPlanUpdateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateHealthPlan,
    onSuccess: () => invalidateHealthPlanRelated(qc),
  });
};

export const useHealthPlanPublishMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: publishHealthPlan,
    onSuccess: () => invalidateHealthPlanRelated(qc),
  });
};

export const useClaimEditMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: claimEditHealthPlan,
    onSuccess: (data) =>
      data.status === 'ready'
        ? invalidateHealthPlanRelated(qc)
        : Promise.resolve(),
  });
};

export const useConsultTransferMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: consultTransferHealthPlan,
    onSuccess: () => invalidateHealthPlanRelated(qc),
  });
};

export const useHealthPlanOriginalReportMutation = () =>
  useMutation({
    mutationFn: getHealthPlanOriginalReport,
  });

export const useHealthPlanIssueLedgerRefreshMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: refreshHealthPlanIssueLedger,
    onSuccess: (_data, planId) =>
      qc.invalidateQueries({ queryKey: healthPlanKeys.issueLedgerAI(planId) }),
  });
};

export const useAiGenerateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: aiGenerateHealthPlan,
    onSuccess: () => invalidateHealthPlanRelated(qc),
  });
};

export const useReviewRemindersAiGenerateMutation = () =>
  useMutation({
    mutationFn: aiGenerateHealthPlanReviewReminders,
  });
